import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatBRL, ESTAGIO_LABELS } from '@/pages/painel/lib/comercial';

interface InteligenciaData {
  kpis: {
    oportunidades_total: number;
    abertas: number;
    ganhas: number;
    pipeline: number;
    lucro_potencial: number;
    orcamentos_sem_fechamento: number;
    reativaveis: number;
    arquitetos: number;
    fornecedores: number;
  };
  por_etapa: Record<string, number>;
  por_segmento: Record<string, number>;
  por_cidade: Record<string, number>;
  por_uf: Record<string, number>;
  motivos_perda: Record<string, number>;
  por_origem_whatsapp: Record<string, number>;
}

export default function InteligenciaComercial() {
  const { password } = useOutletContext<{ password: string }>();
  const [data, setData] = useState<InteligenciaData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
        body: { action: 'inteligencia_comercial', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      if (resp?.error) throw new Error(resp.error);
      setData(resp as InteligenciaData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar inteligência comercial.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const renderBarras = (map: Record<string, number>, labelMap?: Record<string, string>) => {
    const entries = Object.entries(map ?? {});
    if (entries.length === 0) return <p className="text-xs text-gray-400">Sem dados ainda.</p>;
    const max = Math.max(...entries.map(([, v]) => v), 1);
    return (
      <div className="space-y-1.5">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2">
            <span className="text-xs text-gray-600 w-32 truncate shrink-0">{labelMap?.[k] ?? (k === 'nao_informado' ? 'Não informado' : k)}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#E8610A] rounded-full" style={{ width: `${(v / max) * 100}%` }} />
            </div>
            <span className="text-xs text-gray-500 w-6 text-right">{v}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-orange-50 text-orange-600 rounded-xl">
            <i className="ri-brain-line text-lg"></i>
          </div>
          <div>
            <h2 className="font-bold text-gray-900">Inteligência Comercial</h2>
            <p className="text-sm text-gray-500">Leitura de padrões a partir dos dados reais. Sem inventar números.</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer whitespace-nowrap disabled:opacity-60">
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i> Atualizar
        </button>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}

      {loading && !data ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label: 'Oportunidades', value: String(data.kpis.oportunidades_total), icon: 'ri-rocket-2-line' },
              { label: 'Abertas', value: String(data.kpis.abertas), icon: 'ri-hourglass-line' },
              { label: 'Pipeline', value: formatBRL(data.kpis.pipeline), icon: 'ri-funds-line' },
              { label: 'Lucro potencial', value: formatBRL(data.kpis.lucro_potencial), icon: 'ri-coins-line' },
              { label: 'Orçamentos sem fechamento', value: String(data.kpis.orcamentos_sem_fechamento), icon: 'ri-file-list-3-line' },
              { label: 'Reativáveis', value: String(data.kpis.reativaveis), icon: 'ri-restart-line' },
              { label: 'Arquitetos', value: String(data.kpis.arquitetos), icon: 'ri-pen-nib-line' },
              { label: 'Fornecedores', value: String(data.kpis.fornecedores), icon: 'ri-store-3-line' },
              { label: 'Ganhas', value: String(data.kpis.ganhas), icon: 'ri-check-double-line' },
            ].map((k) => (
              <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold flex items-center gap-1.5"><i className={k.icon}></i>{k.label}</p>
                <p className="text-base font-bold text-gray-900 truncate">{k.value}</p>
              </div>
            ))}
          </div>

          {/* Breakdowns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Leads por estágio</h4>
              {renderBarras(data.por_etapa, ESTAGIO_LABELS)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por segmento</h4>
              {renderBarras(data.por_segmento)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por cidade</h4>
              {renderBarras(data.por_cidade)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Por UF</h4>
              {renderBarras(data.por_uf)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Motivos de perda</h4>
              {renderBarras(data.motivos_perda)}
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h4 className="text-sm font-bold text-gray-900 mb-3">Origem (Normal / Business)</h4>
              {renderBarras(data.por_origem_whatsapp, { whatsapp_normal: 'Normal', whatsapp_business: 'Business', ambos: 'Ambos', nao_informado: 'Não informado' })}
            </div>
          </div>

          {/* Pendências aguardando mensagens */}
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-5">
            <h4 className="text-sm font-bold text-gray-900 mb-1">Indicadores que aguardam dados reais</h4>
            <p className="text-xs text-gray-500">
              Produtos mais procurados por mês, soluções compradas juntas, evolução mensal e reativações por período
              serão preenchidos automaticamente quando as mensagens dos dois WhatsApps forem importadas.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}